import React from "react";
import PropTypes from "prop-types";
import { Pagination, PaginationItem, PaginationLink } from "reactstrap";

/**
 * 全站共用的頁碼列 —— 只有這一份,列表頁不要各自刻。
 *
 * 樣式來源是模板既有的 `.pagination-rounded`(`assets/scss/custom/components/_pagination.scss`,
 * Admin `/projects-grid`、`/ecommerce-products` 用的就是它):32px 圓形、無外框、左右各 5px。
 * 設計稿(DCK-06 footer 2128:28853 / 2128:28855)在圓形頁碼外還有一層淺灰圓角面板,
 * 那層放在 `.dck-pager`(`_dck-figma.scss`),不改模板本身。
 * ‹ › 箭頭是 reactstrap `previous`/`next` 的內建字元(U+2039 / U+203A),與稿上一致。
 *
 *   <Pager page={page} pageCount={pageCount} onPageChange={setPage} />
 *
 * 只有一頁時回 null —— 設計稿的 footer 在單頁時只留「顯示 a–b 筆，共 N 筆」。
 *
 * 頁尾整條(筆數 + 頁碼)請用 `<ListFooter>` —— 設計稿(工作項目 2128:22990、
 * 訊息查詢 2128:29130)把它放在白卡**之外**、坐在頁面底色上;不要塞進 Card。
 */
export const Pager = ({ page, pageCount, onPageChange, windowSize = 2 }) => {
  if (!onPageChange || pageCount <= 1) return null;

  // 窗格化頁碼(目前頁前後各 windowSize 頁),頭尾兩頁固定露出,避免頁數多時列爆
  const from = Math.max(1, page - windowSize);
  const to = Math.min(pageCount, page + windowSize);
  const pages = [];
  for (let i = from; i <= to; i += 1) pages.push(i);

  const go = (e, p) => {
    // href="#" 是為了讓頁碼是真的連結(鍵盤可 focus);點下去不要真的跳到 #
    if (e) e.preventDefault();
    if (p >= 1 && p <= pageCount && p !== page) onPageChange(p);
  };

  return (
    <div className="dck-pager">
      {/* reactstrap 的 className 落在外層 <nav>,樣式要吃到 <ul class="pagination"> 得用
          listClassName —— 模板的 .pagination-rounded 是寫在 ul 上的 */}
      <Pagination listClassName="pagination-rounded mb-0">
        <PaginationItem disabled={page <= 1}>
          <PaginationLink previous href="#" onClick={(e) => go(e, page - 1)} />
        </PaginationItem>
        {from > 1 ? (
          <PaginationItem>
            <PaginationLink href="#" onClick={(e) => go(e, 1)}>1</PaginationLink>
          </PaginationItem>
        ) : null}
        {pages.map((p) => (
          <PaginationItem active={p === page} key={p}>
            <PaginationLink href="#" onClick={(e) => go(e, p)}>{p}</PaginationLink>
          </PaginationItem>
        ))}
        {to < pageCount ? (
          <PaginationItem>
            <PaginationLink href="#" onClick={(e) => go(e, pageCount)}>{pageCount}</PaginationLink>
          </PaginationItem>
        ) : null}
        <PaginationItem disabled={page >= pageCount}>
          <PaginationLink next href="#" onClick={(e) => go(e, page + 1)} />
        </PaginationItem>
      </Pagination>
    </div>
  );
};

Pager.propTypes = {
  /** 目前頁(1-based) */
  page: PropTypes.number.isRequired,
  /** 總頁數 */
  pageCount: PropTypes.number.isRequired,
  /** (page) => void;沒給就不渲染 */
  onPageChange: PropTypes.func,
  /** 目前頁前後各露出幾頁 */
  windowSize: PropTypes.number,
};

/**
 * 列表頁尾:「顯示 a–b 筆，共 N 筆」+ `<Pager>`。
 *
 * 統一 UI(Figma DCK-01-02 `2128:22990`、DCK-07-01-02 `2128:29130`):
 * header 與這條 footer 都坐在頁面底色上、**沒有白底**;只有中間內容(工具列+表/列表)
 * 包在白卡裡。所以呼叫端要把它放在 `</Card>` **之後**,不要塞進 CardBody /
 * DataTable。
 *
 *   <Card><CardBody>…toolbar + table…</CardBody></Card>
 *   <ListFooter page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
 */
export const ListFooter = ({ page = 1, pageSize = 10, total = 0, onPageChange }) => {
  if (!onPageChange) return null;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="dck-list-footer">
      {/* 文案依設計稿(DCK-01-01 48:10162):顯示 a–b 筆，共 N 筆;en-dash */}
      <span className="text-muted small">
        顯示 {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} 筆，共 {total} 筆
      </span>
      <Pager page={page} pageCount={pageCount} onPageChange={onPageChange} />
    </div>
  );
};

ListFooter.propTypes = {
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
};

export default Pager;
